import { useEffect, type HTMLAttributes, type ImgHTMLAttributes } from 'react';
import { hasClass } from '@utility/class-name';
import { cn } from '@skupreme/shared/ui';
import { ImageWithFallback } from './FallbackImage';

/** The user's or organisation's first letter, when there is no picture. */
function Initial({
  name,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { name?: string }) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:mr-3 sk:flex sk:items-center sk:justify-center sk:rounded-[inherit] sk:pt-[0.5px]',
        'sk:text-base sk:leading-none sk:font-bold sk:uppercase',
        !hasClass(className, 'w-fit') && 'sk:size-full',
        hasClass(className, 'onboarding') && 'sk:text-[2rem]',
        className,
      )}
    >
      {name ? name[0] : ''}
    </div>
  );
}

export function OrgImage({
  className,
  ...rest
}: ImgHTMLAttributes<HTMLImageElement>) {
  return (
    <img
      {...rest}
      className={cn(
        'sk:size-[25px] sk:min-w-[25px] sk:overflow-hidden sk:rounded-full sk:object-cover',
        className,
      )}
    />
  );
}

export function ImageContainer({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:flex sk:items-center sk:justify-center sk:overflow-hidden sk:rounded-full',
        className,
      )}
    />
  );
}

/** A 25px circle that crops the picture or initial inside it. */
export function AvatarContainer({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:relative sk:size-[25px] sk:min-w-[25px] sk:overflow-hidden sk:rounded-full',
        'sk:bg-fill-secondary sk:text-primary',
        'sk:[&_img]:size-full sk:[&_img]:object-cover',
        className,
      )}
    />
  );
}

interface IImageAvatarProps extends HTMLAttributes<HTMLDivElement> {
  image?: string;
  name?: string;
}

interface IOrganisationAvatarProps extends HTMLAttributes<HTMLDivElement> {
  image?: string;
  name?: string;
  isSummaryView?: boolean;
  onNoImageFunction?: () => void;
}

export function ImageAvatar({ image, name, ...rest }: IImageAvatarProps) {
  return image ? (
    <ImageWithFallback src={image} alt={name} {...rest} />
  ) : (
    <Initial name={name} {...rest} />
  );
}

export function OrganisationAvatar({
  image,
  name,
  isSummaryView = false,
  onNoImageFunction,
  ...rest
}: IOrganisationAvatarProps) {
  useEffect(() => {
    if (!isSummaryView && !image) onNoImageFunction?.();
  }, [image, isSummaryView, onNoImageFunction]);

  if (isSummaryView) {
    return (
      <ImageContainer>
        <OrgImage
          src="/images/icon-skupreme.svg"
          alt={name || 'Summary View'}
          {...rest}
          className={cn('sk:bg-brand sk:p-1.5', rest.className)}
        />
      </ImageContainer>
    );
  }

  return image ? (
    <ImageContainer>
      <OrgImage src={image} alt={name} {...rest} />
    </ImageContainer>
  ) : (
    <Initial name={name} {...rest} />
  );
}
